// src/pages/MonitoringManager.js
import React, { useState, useMemo, useEffect } from 'react';
import '../design/MonitoringManager.css';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchClusterData } from '../slices/clusterSlice';

import { keycloak } from '../auth/keycloak';
import { selectTestbed } from '../slices/testbedSlice';
import { Result, Button } from 'antd';
import { Breadcrumb } from 'antd';
import { Link } from 'react-router-dom';

/* ─── chart.js registration ─── */
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

/* ─── shared chart options ─── */
const barOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'top', labels: { boxWidth: 12, font: { size: 12 } } },
  },
  scales: {
    x: { grid: { display: false }, ticks: { font: { size: 12 } } },
    y: {
      beginAtZero: true,
      grid: { color: '#e5e7eb' },
      ticks: { stepSize: 20, font: { size: 12 } },
    },
  },
  barThickness: 35,
};

/* ─── colour helpers ─── */
const palette = [
  '#e67e22', '#e74c3c', '#3498db', '#2ecc71',
  '#9b59b6', '#1abc9c', '#f1c40f', '#34495e',
];

const colourFromString = (str, prime) =>
  palette[
    [...str].reduce((h, c) => (h * prime + c.charCodeAt(0)) % palette.length, 0)
  ];

const nodeColour = name => colourFromString(name, 31);
const archColour = arch => colourFromString(arch, 29);

const bytesToGiB = b => +(b / 1024 ** 3).toFixed(2);

/* ───────────────────────────────────────────────────────────── */

const TB_LABEL = { tbA: 'INTRA', tbB: 'TMA' };
const GROUP_TO_TBID = { '/testbed1': 'tbA', '/testbed2': 'tbB' };

const MonitoringManager = () => {
  const navigate  = useNavigate();
  const dispatch  = useDispatch();
  const { data: clusterData, loading, error } = useSelector(state => state.cluster);

  // chosen testbed from /choose-testbed (Redux)
  const tbId = useSelector(selectTestbed); // 'tbA' | 'tbB' | null

  // allowed testbeds from Keycloak groups
  const allowedTbIds = useMemo(() => {
    const groups = keycloak.tokenParsed?.groups || [];
    const ids = groups.map(g => GROUP_TO_TBID[g]).filter(Boolean);
    return Array.from(new Set(ids));
  }, []);

  // trial options (disable the non-selected if admin chose one)
  const trialOptions = useMemo(() => {
    const ids = allowedTbIds.length ? allowedTbIds : ['tbA', 'tbB'];
    return ids.map(id => ({
      id,
      label: TB_LABEL[id] || id,
      disabled: tbId ? id !== tbId : false,
    }));
  }, [allowedTbIds, tbId]);

  // initial trial value
  const initialTrialValue = useMemo(() => {
    if (allowedTbIds.length === 1) return allowedTbIds[0];
    if (tbId) return tbId;
    return trialOptions[0]?.id || 'tbA';
  }, [allowedTbIds, tbId, trialOptions]);

  /* form fields */
  const [modelName, setModelName] = useState('TMA');
  const [trial, setTrial] = useState(initialTrialValue);
  const [selectedMaster, setSelectedMaster] = useState('all'); // default view

  /* keep trial synced if allowed set / chosen tb changes */
  useEffect(() => {
    setTrial(prev => {
      // if previous is still valid, keep it; otherwise reset to initial
      const valid = trialOptions.some(o => o.id === prev && (!o.disabled));
      return valid ? prev : initialTrialValue;
    });
  }, [initialTrialValue, trialOptions]);

  /* fetch API once */
  useEffect(() => {
    dispatch(fetchClusterData());
  }, [dispatch]);

  /* flatten all nodes across clusters */
  const allNodes = useMemo(() => {
    if (!clusterData) return {};
    const out = {};
    Object.values(clusterData).forEach(cluster =>
      Object.entries(cluster.nodes || {}).forEach(([id, node]) => {
        out[id] = node;
      })
    );
    return out;
  }, [clusterData]);

  /* ─── build chart datasets ─── */
  const {
    archData,
    memStaticData,
    memUsedPctData,
  } = useMemo(() => {
    const nodes =
      selectedMaster === 'all'
        ? allNodes
        : clusterData?.[selectedMaster]?.nodes || {};

    const labels = Object.keys(nodes).sort();

    /* CPU architecture doughnut */
    const archCount = {};
    labels.forEach(name => {
      const arch = nodes[name].cpuArchitecture || 'unknown';
      archCount[arch] = (archCount[arch] || 0) + 1;
    });
    const archLabels = Object.keys(archCount);
    const archData = {
      labels: archLabels,
      datasets: [
        {
          data           : archLabels.map(a => archCount[a]),
          backgroundColor: archLabels.map(archColour),
        },
      ],
    };

    /* memory totals & used % */
    const memStaticGiB = [];
    const memUsedPct   = [];

    labels.forEach(name => {
      const n   = nodes[name];
      const tot = n.resources.memoryInBytes;
      const free= n.available_resources.memoryInBytes;
      memStaticGiB.push(bytesToGiB(tot));
      memUsedPct.push(Math.round(((tot - free) / tot) * 100));
    });

    const memStaticData = {
      labels,
      datasets: [
        {
          label          : 'Total RAM (GiB)',
          data           : memStaticGiB,
          backgroundColor: labels.map(nodeColour),
        },
      ],
    };

    const memUsedPctData = {
      labels,
      datasets: [
        {
          label          : 'Memory Used (%)',
          data           : memUsedPct,
          backgroundColor: labels.map(nodeColour),
        },
      ],
    };

    return { archData, memStaticData, memUsedPctData };
  }, [allNodes, clusterData, selectedMaster]);

  /* ─── early returns ─── */
  /* ─── early returns ─── */
if (loading === 'pending') {
  return (
    <div className="loading-container">
      <div className="spinner" />
      <p className="loading-text">Loading cluster metrics…</p>
    </div>
  );
}

if (loading === 'failed') {
  return (
    <div className="loading-container">
      <Result
        status="error"
        title="Could not load cluster metrics"
        subTitle={
          <>
            We were unable to retrieve the cluster metrics from the testbed.
            <br />
            This may mean the testbed is offline or the connection is misconfigured.
          </>
        }
         extra={
        <Button
          type="primary"
          onClick={() => navigate('/home')}
          style={{ cursor: 'pointer' }}
        >
          Back Home
        </Button>
      }
      >
        {error && (
          <div className="error-details">
            <span style={{ fontSize: 12, opacity: 0.7 }}>
              Technical details: {String(error)}
            </span>
          </div>
        )}
      </Result>
    </div>
  );
}

if (!clusterData) return null;

  
  /* ─── UI ─── */
  return (
    <>

     <div className="page-header">
      <div className="page-breadcrumb-wrapper">
        <Breadcrumb>
          <Breadcrumb.Item>
            <Link to="/home">Home</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Monitoring Manager</Breadcrumb.Item>
        </Breadcrumb>
      </div>
    </div>
      <div className="monitoring-container">
        <div className="header-section">
          {/* Trial select (same behavior as in Application form) */}
          <div className="dropdown" style={{ marginBottom: 12 }}>
            <label>Testbed:&nbsp;</label>
            <select
              value={trial}
              onChange={(e) => setTrial(e.target.value)}
              disabled={allowedTbIds.length === 1} // lock if user has only one
            >
              {trialOptions.map(opt => (
                <option key={opt.id} value={opt.id} disabled={opt.disabled}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* kept as is: model name text input */}

          {/* removed the description textarea as requested */}
          {/* (everything else unchanged) */}

          <div className="dropdown">
            <label>Master:</label>
            <select
              value={selectedMaster}
              onChange={e => setSelectedMaster(e.target.value)}
            >
              <option value="all">all</option>
              {Object.keys(clusterData).map(master => (
                <option key={master} value={master}>
                  {master}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="charts-section">
          {/* CPU architecture distribution */}
          <div className="chart-card">
            <h3>CPU&nbsp;Architectures</h3>
            <div className="chart-wrapper">
              <Doughnut
                data={archData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { position: 'top', labels: { boxWidth: 12, font: { size: 12 } } } }
                }}
              />
            </div>
          </div>

          {/* Total RAM per node */}
          <div className="chart-card">
            <h3>Total&nbsp;Memory&nbsp;(GiB)</h3>
            <div className="chart-wrapper">
              <Bar data={memStaticData} options={barOptions} />
            </div>
          </div>

          {/* Used-memory % per node */}
          <div className="chart-card">
            <h3>Memory&nbsp;Used&nbsp;%</h3>
            <div className="chart-wrapper">
              <Bar data={memUsedPctData} options={barOptions} />
            </div>
          </div>
        </div>

        <div className="infrastructure-button-container">
          <button
            className="infrastructure-button"
            onClick={() => navigate('/dashboard')}
          >
            Infrastructure Details
          </button>
        </div>
      </div>
    </>
  );
};

export default MonitoringManager;
